<div class="certificate-query__wrapper">
  <div class="certificate-query__card">
    <div class="certificate-query__card-label">{{ 'certificateQuery.periodLabel' | translate }}</div>

    <div class="certificate-query__quick-ranges">
      @for (range of QUICK_RANGES; track range.key) {
        <button
          class="certificate-query__chip"
          [class.certificate-query__chip--active]="activeChip === range.key"
          (click)="selectQuickRange(range.key)"
        >
          @if (range.key === 'custom') {
            <mat-icon class="certificate-query__chip-icon">calendar_today</mat-icon>
          }
          {{ range.labelKey | translate }}
        </button>
      }
    </div>

    <div class="certificate-query__filter">
      <div class="certificate-query__dates" [formGroup]="dateRange">
        <div class="certificate-query__date-field">
          <label class="certificate-query__field-label">{{ 'certificateQuery.dateFrom' | translate }} *</label>
          <mat-form-field class="certificate-query__date-field__input" appearance="outline" subscriptSizing="dynamic">
            <input matInput [matDatepicker]="startPicker" formControlName="start"
                   [placeholder]="'certificateQuery.datePlaceholder' | translate"
                   (input)="activeChip = 'custom'">
            <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>
        </div>

        <span class="certificate-query__date-sep">—</span>

        <div class="certificate-query__date-field">
          <label class="certificate-query__field-label">{{ 'certificateQuery.dateTo' | translate }}</label>
          <mat-form-field class="certificate-query__date-field__input" appearance="outline" subscriptSizing="dynamic">
            <input matInput [matDatepicker]="endPicker" formControlName="end"
                   [placeholder]="'certificateQuery.dateTodayPlaceholder' | translate"
                   (input)="activeChip = 'custom'">
            <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
        </div>
      </div>

      <div class="certificate-query__cert-wrapper">
        <label class="certificate-query__field-label">{{ 'certificateQuery.certificate' | translate }}</label>
        <div class="certificate-query__cert-row">
          <app-ci-multiselect
            class="certificate-query__cert-select"
            [items]="ALL_CERTS"
            [(ngModel)]="selectedCerts"
            [summary]="true"
            [summaryTemplate]="
              selectedCerts.length === ALL_CERTS.length
                ? ('certificateQuery.allCertificates' | translate)
                : '{selected} ' + ('certificateQuery.certsSelected' | translate)
            "
            [placeholder]="'certificateQuery.allCertificates' | translate"
            [searchPlaceholder]="'certificateQuery.certSearch' | translate"
            [ariaLabel]="'certificateQuery.certificate' | translate"
            [clearLabel]="'certificateQuery.resetFilter' | translate"
            [emptyLabel]="'ciSelect.noMatch' | translate"
            (openChange)="onCertSelectOpen($event)"
          ></app-ci-multiselect>
          @if (hasCertFilter) {
            <button
              class="certificate-query__cert-reset-btn"
              type="button"
              [attr.aria-label]="'certificateQuery.resetFilter' | translate"
              [matTooltip]="'certificateQuery.resetFilter' | translate"
              (click)="resetCerts()"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>
      </div>

      <button
        class="certificate-query__search-btn"
        [class.certificate-query__search-btn--disabled]="!dateRange.value.start || loading"
        [disabled]="!dateRange.value.start || loading"
        (click)="search()"
      >
        {{ 'certificateQuery.search' | translate }}
      </button>
    </div>

    @if (searched && dataSource.data.length > 0) {
      <div class="certificate-query__quick-filter">
        <span class="certificate-query__field-label">{{ 'certificateQuery.statusFilter' | translate }}</span>
        <div class="certificate-query__status-chips">
          @for (status of STATUS_FILTERS; track status) {
            <button
              type="button"
              class="certificate-query__status-chip certificate-query__status-chip--{{ status }}"
              [class.certificate-query__status-chip--active]="isStatusSelected(status)"
              (click)="toggleStatusFilter(status)"
            >
              <mat-icon class="certificate-query__event-icon">{{ STATUS_CONFIG[status].icon }}</mat-icon>
              <span>{{ STATUS_CONFIG[status].labelKey | translate }}</span>
              <span class="certificate-query__status-chip-count">{{ getStatusCount(status) }}</span>
            </button>
          }
        </div>
      </div>
    }
  </div>

  @if (!searched) {
    <p class="certificate-query__hint">{{ 'certificateQuery.hint' | translate }} <strong>{{ 'certificateQuery.hintAction' | translate }}</strong>.</p>
  }

  @if (searched && dataSource.data.length === 0) {
    <p class="certificate-query__no-data">{{ 'certificateQuery.noData' | translate }}</p>
  }

  @if (dataSource.data.length > 0) {
    <div class="certificate-query__results-summary">
      <div class="certificate-query__summary-left">
        <span class="certificate-query__summary-count">{{ dataSource.data.length }} {{ 'certificateQuery.events' | translate }}</span>
        <span class="certificate-query__summary-range">
          {{ appliedFrom | date:'dd.MM.yyyy' }} – {{ appliedTo | date:'dd.MM.yyyy' }}
        </span>
        @if (appliedCerts.length === 1) {
          <span class="certificate-query__summary-cert-filter">· {{ appliedCerts[0] }}</span>
        } @else if (appliedCerts.length > 1) {
          <span class="certificate-query__summary-cert-filter">· {{ 'certificateQuery.certFilterCount' | translate: {count: appliedCerts.length} }}</span>
        }
      </div>
    </div>

    <mat-form-field appearance="outline" class="certificate-query__search">
      <mat-label>{{ 'certificateQuery.filterByConsultant' | translate }}</mat-label>
      <input matInput [(ngModel)]="filterTerm" (ngModelChange)="applyFilter()" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <mat-table [dataSource]="dataSource" matSort matSortActive="certificationDate" matSortDirection="desc" matSortDisableClear class="certificate-query__table">

      <ng-container matColumnDef="certificationDate">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'certificateQuery.certificationDate' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="certificate-query__muted-cell">{{ row.certificationDate | date: 'dd.MM.yyyy' }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="consultantName">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'certificateQuery.consultantName' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row">{{ row.consultantName }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="position">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'certificateQuery.position' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row">{{ row.position }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="certificationName">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'certificateQuery.certificationName' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row"
          [matTooltip]="getCertFullName(row.certificationName)"
          [matTooltipShowDelay]="300">{{ row.certificationName }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="hireDate">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'certificateQuery.hireDate' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="certificate-query__muted-cell">{{ row.consultantSince | date: 'dd.MM.yyyy' }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="status">
        <mat-header-cell *matHeaderCellDef>{{ 'certificateQuery.event' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row">
          @if (row.status && row.status !== 'none' && STATUS_CONFIG[row.status]) {
            <span class="certificate-query__event-cell certificate-query__event-cell--{{ row.status }}">
              <mat-icon class="certificate-query__event-icon">{{ STATUS_CONFIG[row.status].icon }}</mat-icon>
              {{ STATUS_CONFIG[row.status].labelKey | translate }}
            </span>
          }
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
      <mat-row *matRowDef="let row; columns: displayedColumns;"></mat-row>
    </mat-table>
  }
</div>
